import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import './users.css';

const API_URL = import.meta.env.VITE_API_URL;

/* ===============================================================
   定数定義
   =============================================================== */

/** ID列を表示するかどうか（true: 表示 / false: 非表示） */
const SHOW_ID_COL = false;

/** テーブルの列数（ID列あり: 7 / なし: 6） */
const COLUMNS_COUNT = SHOW_ID_COL ? 7 : 6;

/* ===============================================================
   カードコンポーネント（スマホ表示用）
   =============================================================== */

/**
 * ユーザーカード（テーブル行のスマホ表示版）
 * @param {Object} u - ユーザーオブジェクト
 * @param {boolean} isMe - 自分自身かどうか
 * @param {Function} onToggleActive - 有効/停止切り替え時のコールバック
 */
const UserCard = ({ u, isMe, onToggleActive }) => (
  <div className={`au-card ${!u.is_active ? 'au-card--inactive' : ''} ${isMe ? 'au-card--me' : ''}`}>
    {SHOW_ID_COL && (
      <div className="au-card__row">
        <span className="au-card__label">ID</span>
        <span className="au-card__value">{u.user_id}</span>
      </div>
    )}
    <div className="au-card__row">
      <span className="au-card__label">名前</span>
      <span className="au-card__value">{u.user_name}</span>
    </div>
    <div className="au-card__row">
      <span className="au-card__label">メール</span>
      <span className="au-card__value">{u.user_email}</span>
    </div>
    <div className="au-card__row">
      <span className="au-card__label">電話番号</span>
      <span className="au-card__value">{u.phone || '-'}</span>
    </div>
    <div className="au-card__row">
      <span className="au-card__label">最終ログイン</span>
      <span className="au-card__value">
        {u.last_login ? new Date(u.last_login).toLocaleString() : '-'}
      </span>
    </div>
    <div className="au-card__row">
      <span className="au-card__label">権限</span>
      <span className="au-card__value">{u.is_active ? '有効' : '無効'}</span>
    </div>
    <div className="au-card__actions">
      <button
        className="btn danger"
        onClick={() => onToggleActive(u)}
        disabled={isMe}
        title={u.is_active ? '無効化' : '有効化'}
      >
        {u.is_active ? '権限無効' : '有効化'}
      </button>
    </div>
  </div>
);

/* ===============================================================
   メインコンポーネント
   =============================================================== */

const AdminUsers = () => {
  /* ---------------------------------------------------------------
     State定義
     --------------------------------------------------------------- */
  
  // ユーザー一覧関連
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  
  // フィルター関連
  const [q, setQ] = useState('');  // 検索クエリ（名前/メール/電話番号）

  // 招待URL関連
  const [inviteUrl, setInviteUrl] = useState('');
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [copied, setCopied] = useState(false);

  // 自分判定用（ログイン時にsessionStorageに保存されたメールアドレス）
  const meEmail = sessionStorage.getItem('WuserAddress') || '';

  
  /* ---------------------------------------------------------------
     API呼び出し関数
     --------------------------------------------------------------- */
  
  /**
   * ユーザー一覧を取得
   */
  const fetchList = async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await axios.get(`${API_URL}/api/admin/users_list/`);
      if (res.data?.success) {
        setRows(res.data.items || []);
      } else {
        setErr('一覧取得に失敗しました');
      }
    } catch (e) {
      console.error(e);
      setErr('一覧取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 既存の招待URLがあるかチェック
   */
  const checkInviteUrl = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/invitations/check/`);
      if (res.data?.success && res.data.url) {
        setInviteUrl(res.data.url);
      }
    } catch (e) {
      console.error('招待リンク確認に失敗しました', e);
    }
  };

  /**
   * ユーザーの有効/停止状態を切り替え
   * @param {Object} u - ユーザーオブジェクト
   */
  const toggleActive = async (u) => {
    // 自分は有効/停止の操作不可
    if ((u.user_email || '') === meEmail) return;

    const actionText = u.is_active ? '停止' : '有効化';
    if (!window.confirm(`ユーザー「${u.user_name}」を${actionText}しますか？`)) return;
    
    try {
     
      await axios.patch(
        `${API_URL}/api/admin/users_update_status/${u.user_id}/`,
        { is_active: !u.is_active }
      );
      fetchList();
    } catch (e) {
      console.error(e);
      alert('ステータス更新に失敗しました');
    }
  };

  /* ---------------------------------------------------------------
     招待URL関連の関数
     --------------------------------------------------------------- */
  
  /**
   * 開催者招待URLを作成
   */
  const createOrganizerInvite = async () => {
    // すでにURLがあるなら何もしない
    if (inviteUrl) return;
    
    setCopied(false);
    setCreatingInvite(true);
    setErr('');
    
    try {
     
      const res = await axios.post(
        `${API_URL}/api/admin/invitations/`);

      const url = res?.data?.url || '';
      if (!url) throw new Error('URLが取得できませんでした');
      setInviteUrl(url);
    } catch (e) {
      console.error(e);
      setErr('招待URLの作成に失敗しました');
      setInviteUrl('');
    } finally {
      setCreatingInvite(false);
    }
  };

  /**
   * 招待URLをクリップボードにコピー
   */
  const copyInviteUrl = async () => {
    try {
      if (inviteUrl) {
        await navigator.clipboard.writeText(inviteUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }
    } catch {
      // フォールバック: execCommandを使用
      const ta = document.createElement('textarea');
      ta.value = inviteUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  /**
   * 招待URLをクリア（削除）
   */
  const clearInvite = async () => {
    setErr('');

    // URLが無ければ何もしない
    if (!inviteUrl) return;

    const ok = window.confirm(
      'すでに招待URLが発行されています。\n再取得するとURLは変更されます。\nクリアしてよろしいですか？'
    );
    if (!ok) return;

    try {
      // URLからトークンを抽出
      const t = new URL(inviteUrl).searchParams.get('t') || '';
      if (t) {
       
        await axios.delete(`${API_URL}/api/admin/invitations/delete/`, {
          data: { token: t }
        });
      }
      // UIクリア
      setInviteUrl('');
      setCopied(false);
    } catch (e) {
      console.error(e);
      setErr('招待URLの削除に失敗しました');
    }
  };

  /* ---------------------------------------------------------------
     useMemo（計算結果のメモ化）
     --------------------------------------------------------------- */
  
  /**
   * フィルタリングされたユーザー一覧
   * 名前、メール、電話番号で検索
   */
  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return rows;
    
    return rows.filter(r =>
      (r.user_name || '').toLowerCase().includes(qq) ||
      (r.user_email || '').toLowerCase().includes(qq) ||
      (r.phone || '').toLowerCase().includes(qq)
    );
  }, [rows, q]);

  /* ---------------------------------------------------------------
     useEffect（副作用処理）
     --------------------------------------------------------------- */
  
  // 初回ロード時にユーザー一覧と招待URLをチェック
  useEffect(() => {
    fetchList();
    checkInviteUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------------------------------------------------------
     レンダリング関数
     --------------------------------------------------------------- */
  
  /**
   * ツールバー（タイトル、検索、再読込ボタン）を表示
   */
  const renderToolbar = () => (
    <div className="toolbar">
      <div className="listTitle">開催者管理</div>
      
      {/* 検索入力 */}
      <input
        className="searchInput1"
        placeholder="名前 / メール / 電話番号で検索"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      
      {/* 再読込ボタン */}
      <button className="btn" onClick={fetchList} disabled={loading}>
        {loading ? '更新中…' : '再読込'}
      </button>
    </div>
  );

  /**
   * エラーメッセージを表示
   */
  const renderError = () => (
    err && <div className="error-alert">{err}</div>
  );

  /**
   * 招待URL表示エリアを表示
   */
  const renderInviteUrlBox = () => (
    <div className="inviteUrlBox">
      <div className="listTitle">招待URL</div>
      
      {inviteUrl ? (
        <>
          {/* URL入力欄（読み取り専用） */}
          <input
            className="inviteUrlInput"
            value={inviteUrl}
            readOnly
            onFocus={(e) => e.currentTarget.select()}
            aria-label="招待URL"
          />
          
          {/* コピーボタン */}
          <button className="btn" onClick={copyInviteUrl}>
            {copied ? 'コピー済' : 'コピー'}
          </button>
          
          {/* クリアボタン */}
          <button className="btn" onClick={clearInvite} title="URL表示を閉じる">
            クリア
          </button>
        </>
      ) : (
        <div className="inviteHint">
          「開催者として招待」を押すと招待URLが作成され、ここに表示されます。
        </div>
      )}
    </div>
  );

  /**
   * 招待ボタンを表示
   */
  const renderInviteButton = () => (
    <div className="inviteControls inline">
      <button
        className="btn primary"
        onClick={createOrganizerInvite}
        disabled={creatingInvite || !!inviteUrl}
        title={inviteUrl ? '既にURLがあります（クリアで削除）' : '開催者として招待リンクを作成'}
      >
        {creatingInvite ? '作成中…' : '開催者として招待'}
      </button>
    </div>
  );

  /**
   * ステータス行（招待URL表示と招待ボタン）を表示
   */
  const renderStatusRow = () => (
    <div className="statusRow">
      {/* 左側：招待URL表示エリア */}
      <div className="statusLeft">
        {renderInviteUrlBox()}
      </div>

      {/* 右側：招待ボタン */}
      <div className="statusRight">
        {renderInviteButton()}
      </div>
    </div>
  );

  /**
   * テーブルヘッダーを表示
   */
  const renderTableHeader = () => (
    <thead>
      <tr>
        {SHOW_ID_COL && <th>ID</th>}
        <th>名前</th>
        <th>メール</th>
        <th>電話番号</th>
        <th>最終ログイン</th>
        <th>権限</th>
        <th>操作</th>
      </tr>
    </thead>
  );

  /**
   * テーブル行（ユーザー情報）を表示
   * @param {Object} u - ユーザーオブジェクト
   */
  const renderUserRow = (u) => {
    const isMe = (u.user_email || '') === meEmail;
    
    return (
      <tr
        key={u.user_id}
        className={`${!u.is_active ? 'is-inactive' : ''} ${isMe ? 'is-me' : ''}`}
      >
        {/* ID列（設定により表示/非表示） */}
        {SHOW_ID_COL && <td>{u.user_id}</td>}
        
        {/* 名前 */}
        <td>{u.user_name}</td>
        
        {/* メール */}
        <td>{u.user_email}</td>
        
        {/* 電話番号 */}
        <td>{u.phone || '-'}</td>
        
        {/* 最終ログイン */}
        <td>{u.last_login ? new Date(u.last_login).toLocaleString() : '-'}</td>
        
        {/* 権限（有効/無効） */}
        <td>{u.is_active ? '有効' : '無効'}</td>
        
        {/* 操作ボタン */}
        <td className="ops">
          <button
            className="btn danger"
            onClick={() => toggleActive(u)}
            disabled={isMe}
            title={u.is_active ? '無効化' : '有効化'}
          >
            {u.is_active ? '権限無効' : '有効化'}
          </button>
        </td>
      </tr>
    );
  };

  /**
   * テーブルボディを表示
   */
  const renderTableBody = () => (
    <tbody>
      {/* データなしの場合 */}
      {filtered.length === 0 && !loading && (
        <tr>
          <td colSpan={COLUMNS_COUNT} className="empty">対象がありません</td>
        </tr>
      )}
      
      {/* ユーザー一覧 */}
      {filtered.map(u => renderUserRow(u))}
    </tbody>
  );

  /**
   * ユーザーテーブル全体を表示
   */
  const renderTable = () => (
    <div className="tableWrap">
      <table className={`table ${SHOW_ID_COL ? 'has-id' : 'no-id'}`}>
        {renderTableHeader()}
        {renderTableBody()}
      </table>
    </div>
  );

  /**
   * ユーザーカード一覧を表示（スマホ表示用）
   */
  const renderUserCards = () => (
    <div className="au-cardList">
      {filtered.length === 0 && !loading && (
        <div className="empty">対象がありません</div>
      )}
      {filtered.map(u => (
        <UserCard
          key={u.user_id}
          u={u}
          isMe={(u.user_email || '') === meEmail}
          onToggleActive={toggleActive}
        />
      ))}
    </div>
  );

  /* ---------------------------------------------------------------
     メインレンダリング
     --------------------------------------------------------------- */
  return (
    <div className="adminUsers">
      {/* ツールバー */}
      {renderToolbar()}

      {/* エラー表示 */}
      {renderError()}

      {/* ステータス行（招待URL・招待ボタン） */}
      {renderStatusRow()}

      {/* ユーザーテーブル */}
      {renderTable()}
      {renderUserCards()}
    </div>
  );
};

export default AdminUsers;